import { systemMessage } from '@/i18n/systemMessage'
/** Display-only translation. Stored task errors and Agent diagnostics stay intact. */
export function taskErrorMessage(message?: string): string {
  const text = (message || '').trim()
  if (!text) return ''
  if (/\bAGENT_EXECUTION_INTERRUPTED\b/.test(text)) {
    return systemMessage(/read-only executor.*Agent shutdown.*not replayed/i.test(text)
      ? '只读检查因 Agent 停止或重启而中断，未自动重试；不代表操作系统安装失败。请检查当前系统状态，可结束本次任务并保留日志。'
      : 'Agent 任务执行中断，请查看任务日志及目标服务器当前状态，不能仅凭此判断系统安装结果。')
  }
  const isIPMI = /\bIPMI\b|\bIPMI_[A-Z_]+\b|RAKP\s+2\s+HMAC/i.test(text)
  const isRedfish = /\bRedfish\b|\bREDFISH_[A-Z_]+\b/i.test(text)
  if (!isIPMI && !isRedfish && !/\bBMC_(CONTROL_UNAVAILABLE|RESET_UNSUPPORTED)\b/.test(text)) return systemMessage(text)

  let explanation = ''
  const handshakeFailed = /\bIPMI_AUTH_HANDSHAKE_FAILED\b|RAKP\s+2\s+HMAC\s+is\s+invalid/i.test(text)
  const explicitAuthRejection = /invalid password|authentication failed|unauthorized name|invalid user/i.test(text)
  // Classify the outer, combined failure before looking at either channel's
  // nested diagnostics. An embedded 401/unauthorized is not the whole result.
  if (/\bBMC_CONTROL_UNAVAILABLE\b/.test(text)) {
    explanation = 'IPMI 与控制卡原生接口均未通过只读验证，请检查控制卡服务、网络和访问配置；不能仅凭其中一条诊断判定密码错误。'
  } else if (/\bBMC_RESET_UNSUPPORTED\b/.test(text)) {
    explanation = '当前控制卡原生接口暂未提供可用的管理控制器重启动作。'
  } else if (isRedfish && (!isIPMI || /\bREDFISH_[A-Z_]+\b/i.test(text))) {
    if (/\bREDFISH_AUTH_FAILED\b|\bHTTP\s*401\b/i.test(text)) {
      explanation = '控制卡原生接口认证被拒绝，请核对该接口的账号凭据和访问策略。'
    } else if (/\bREDFISH_PERMISSION_DENIED\b|\bHTTP\s*403\b/i.test(text)) {
      explanation = '控制卡原生接口拒绝了当前账号的操作权限，请检查授权。'
    } else if (/\bREDFISH_TRANSPORT_(FAILED|UNAVAILABLE)\b/i.test(text)) {
      explanation = '控制卡原生接口连接或请求失败，请检查网络、接口服务及 TLS 协议兼容性。'
    } else if (/\bREDFISH_WRITE_FAILED\b/i.test(text)) {
      explanation = '控制卡原生接口写请求未成功，请查看任务日志中的返回状态。'
    } else if (/\bREDFISH_READ_FAILED\b/i.test(text)) {
      explanation = '读取控制卡原生接口状态失败，不能据此确认操作是否完成。'
    } else if (/\bREDFISH_RESPONSE_INVALID\b/i.test(text)) {
      explanation = '控制卡原生接口返回的状态无法解析，请查看任务诊断。'
    } else if (/\bREDFISH_ACTION_UNSUPPORTED\b/i.test(text)) {
      explanation = '控制卡原生接口未提供对应的电源动作。'
    } else if (/\bREDFISH_PXE_UNSUPPORTED\b/i.test(text)) {
      explanation = '控制卡原生接口未提供一次性网络启动选项。'
    } else if (/\bREDFISH_SYSTEM_AMBIGUOUS\b/i.test(text)) {
      explanation = '控制卡原生接口未能确定唯一的目标系统。'
    } else if (/\bREDFISH_(TARGET|RESOURCE|REQUEST|BOOT_MODE)_INVALID\b/i.test(text)) {
      explanation = '控制卡原生接口的目标、资源或请求参数无效，请查看任务诊断。'
    }
  } else if (handshakeFailed && !explicitAuthRejection) {
    // Older Agents labelled this AUTH_FAILED. HMAC alone cannot distinguish
    // rejected credentials from firmware/cipher handshake incompatibility.
    explanation = 'IPMI 认证握手校验未通过，可能涉及固件或加密协议兼容性，不能仅凭此判定密码错误；BMC 网页可登录也不能证明 IPMI 通道已通过验证。'
  } else if (/\bIPMI_AUTH_FAILED\b/i.test(text) || (isIPMI && explicitAuthRejection)) {
    explanation = 'BMC 认证未通过，请核对控制卡账号、密码及 IPMI 访问配置。'
  } else if (/\bIPMI_PERMISSION_DENIED\b/i.test(text)) {
    explanation = 'BMC 拒绝了当前账号的 IPMI 操作权限，请检查账号权限。'
  } else if (/\bIPMI_NEGOTIATION_FAILED\b/i.test(text)) {
    explanation = 'IPMI 加密协议协商失败，请检查 BMC 支持的加密套件及固件兼容性。'
  } else if (/\bIPMI_TIMEOUT\b/i.test(text)) {
    explanation = 'IPMI 连接或命令执行超时，请检查 Agent 到 BMC 的网络及 BMC 响应情况。'
  } else if (/\bIPMI_SESSION_FAILED\b|Unable to establish IPMI v2\b/i.test(text)) {
    explanation = '无法建立 IPMI 管理连接，请检查 BMC 的 IPMI 服务、访问配置及网络连通性。'
  } else if (/\bIPMI_COMMAND_FAILED\b/i.test(text)) {
    explanation = 'IPMI 命令执行失败，请查看任务日志中的原始诊断。'
  } else if (/\bIPMI_PREFLIGHT_FAILED\b/i.test(text)) {
    explanation = 'BMC 操作前检查未通过，请查看任务日志中的具体原因。'
  }
  if (!explanation) return systemMessage(text)

  return systemMessage(explanation) + reportedTaskEffects(text).map(systemMessage).join('')
}

/** Keep explicit side-effect facts, not the surrounding raw command output. */
function reportedTaskEffects(text: string): string[] {
  const effects: string[] = []
  const writeAttempted = /已.{0,8}(?:发送|提交|执行|写入|受理|接受)|(?:请求|命令|操作).{0,6}已(?:发送|提交|受理|接受)|写请求结果未确认|\bHTTP\s*202\b|command accepted/i.test(text)
  for (const part of text.split(/[；;。\r\n]+/)) {
    const clause = part.replace(/^\s*[A-Z][A-Z0-9_]*\s*[:：]\s*/, '').trim()
    if (!clause) continue
    if (clause.includes('尚未启用 PXE、未重启')) {
      if (!writeAttempted) effects.push('本次任务尚未启用网络启动，也未重启服务器。')
      continue
    }
    // A later write outcome can contain an earlier preflight diagnostic.
    // Do not turn that nested old claim into a statement that no write occurred.
    if (writeAttempted && /(?:尚未|未)发送|未重启/.test(clause) && !/已发送|结果未确认/.test(clause)) continue
    const hasEffect = /(?:已|未|尚未).{0,16}(?:发送|提交|受理|接受|写入|重启|重发|重复|切换|替代|执行)|(?:请求|命令|设置|操作).{0,6}(?:已|未|尚未)(?:发送|提交|受理|接受|执行|写入|确认)|结果.{0,6}(?:未确认|未知|不明)|不会代替/.test(clause)
    // Only retain human-readable Chinese clauses and familiar protocol labels.
    // No stderr/stdout, command line, nested English auth error or URL is copied.
    const withoutLabels = clause.replace(/\b(?:IPMI|BMC|PXE|Redfish|TLS|HTTP|EFI|UEFI|BIOS)\b/gi, '')
    if (hasEffect && /[\u4e00-\u9fff]/.test(clause) && !/[a-z]/i.test(withoutLabels) && !effects.includes(clause + '。')) {
      effects.push(clause + '。')
    }
  }
  return effects
}
